import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Modal,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import ScanDashboardMatchDetails from './ScanDashboardMatchDetails'
import axios from 'axios'

interface FundingNeedScanModalProps {
  open: boolean
  onClose: () => void
  needId: string
  needTitle: string
}

export default function FundingNeedScanModal({
  open,
  onClose,
  needId,
  needTitle,
}: FundingNeedScanModalProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [latestBatchNumber, setLatestBatchNumber] = useState<number | null>(
    null
  )
  const [orgName, setOrgName] = useState<string>('')

  const fetchLatestScanData = async () => {
    if (!needId || !open) return

    setLoading(true)
    setError(null)

    try {
      // Use direct axios call to get the most recent scan for this funding need
      const apiUrl = import.meta.env.VITE_API_URL
      const url = `${apiUrl}/scans/`
      const params = {
        need_id: needId,
        _start: '0',
        _end: '1',
        _sort: 'scan_date',
        _order: 'desc',
        timeframe: 'all',
      }
      const response = await axios.get(url, { params })

      if (response?.data && response.data.length > 0) {
        const latestScan = response.data[0]
        setLatestBatchNumber(latestScan.batch_number)
        setOrgName(latestScan.org_name || '')
      } else {
        setError('No scan results found for this funding need')
      }
    } catch (err) {
      console.error('Failed to fetch latest scan data:', err)
      setError('Failed to load scan results')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      fetchLatestScanData()
    }
  }, [open, needId])

  return (
    <>
      {loading ? (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby="funding-need-scan-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '400px',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body1">Loading scan results...</Typography>
          </Box>
        </Modal>
      ) : error ? (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby="funding-need-scan-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '90%',
              maxWidth: 600,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'auto',
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                Scan Results for "{needTitle}"
              </Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Error Content */}
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          </Box>
        </Modal>
      ) : latestBatchNumber ? (
        <ScanDashboardMatchDetails
          dialogOpen={open}
          handleCloseDialog={onClose}
          selectedScanBatch={latestBatchNumber}
          orgName={orgName}
          fundingNeedTitle={needTitle}
          fundingNeedId={parseInt(needId)}
          scanDate=""
        />
      ) : null}
    </>
  )
}
